import type { CompanySettings, Invoice, PacaSettings } from '@morales/shared';
import type { ReactNode } from 'react';
import { formatDate, formatMoney, formatQty, formatStamp } from '../format';
import { useLocale } from '../i18n';
import { OakMark } from './brand';

/** PACA trust notice (7 U.S.C. 499e(c)): statutory English, printed on every invoice. docs/VISION.md, "What we are missing" 3. */
export const PACA_NOTICE =
  'The perishable agricultural commodities listed on this invoice are sold subject to the statutory trust authorized by section 5(c) of the ' +
  'Perishable Agricultural Commodities Act, 1930 (7 U.S.C. 499e(c)). The seller of these commodities retains a trust claim over these commodities, ' +
  'all inventories of food or other products derived from these commodities, and any receivables or proceeds from the sale of these commodities ' +
  'until full payment is received.';

/** The interest and attorney-fee clause the owner set (decision 57), as printed under the trust notice. */
export function pacaClause(s: PacaSettings): string {
  const parts: string[] = [];
  if (s.interestPct > 0)
    parts.push(
      `Balances not paid when due accrue interest at ${s.interestPct}% per year from the due date until paid.`,
    );
  if (s.attorneyFees)
    parts.push(
      'The buyer agrees to pay all costs of collection, including reasonable attorney fees, which are sums owing in connection with this transaction under the PACA trust.',
    );
  return parts.join(' ');
}

/** Only while an invoice is on the page: the browser's margin (where it prints the address and date) goes to zero. */
const PRINT_PAGE = '@media print { @page { size: letter; margin: 0; } }';

/** What every place that shows an invoice has: the office, the route's print, the emailed link, the portal. */
export type PaperInvoice = Pick<
  Invoice,
  | 'invoiceNo'
  | 'customerName'
  | 'orderNo'
  | 'termsDays'
  | 'invoiceDate'
  | 'dueDate'
  | 'status'
  | 'subtotal'
  | 'tax'
  | 'total'
  | 'paid'
  | 'balance'
  | 'notes'
  | 'billTo'
  | 'shipTo'
  | 'test'
> & {
  lines: Array<Omit<Invoice['lines'][number], 'itemId'>>;
};

/**
 * The invoice as a document: the same sheet on the screen, on paper, on the emailed link and on the portal. The
 * letterhead and the invoice's number and dates on top, who it is billed (and shipped) to, one table of lines, the
 * totals, then the notes, the proof of delivery and the PACA notice. Nothing on it that a customer would not read.
 */
export function InvoicePaper({
  inv,
  paca,
  company,
  delivery,
  signatureUrl,
  headingLevel = 1,
  children,
}: {
  inv: PaperInvoice;
  paca: PacaSettings | null | undefined;
  company: CompanySettings | null | undefined;
  delivery?: Invoice['delivery'];
  signatureUrl?: string;
  /** 2 where the page already has its own title (the office screen, the route's print) */
  headingLevel?: 1 | 2;
  children?: ReactNode;
}) {
  const { t, intlLocale } = useLocale();
  const money = (v: number) => formatMoney(v, intlLocale);
  const day = (d: string) => formatDate(d, intlLocale, 'date');
  const cityLine = company
    ? [[company.city, company.state].filter(Boolean).join(', '), company.zip].filter(Boolean).join(' ')
    : '';
  const contact = company ? [company.phone, company.email].filter(Boolean).join(' · ') : '';
  // paid off, or paid more than it came to after a short or an edit (the rest is money on account)
  const paidOff = inv.status === 'issued' && inv.balance !== null && inv.balance <= 0 && inv.total > 0;
  // PACA wording prints once the owner has a PACA license on file (JL, 2026-10-24: not before, so customers are not
  // shown a trust notice that does not apply yet)
  const licensed = !!paca?.licenseNo;
  const clause = licensed && paca ? pacaClause(paca) : '';
  return (
    // On paper the browser's own margin is set to nothing (so it prints no web address, date or title there) and the
    // margins are drawn here instead: the frame's head and foot repeat on every page, the sides are its padding
    <table className="inv-frame" role="presentation">
      <thead role="presentation">
        <tr role="presentation">
          <td role="presentation" className="inv-frame-top" />
        </tr>
      </thead>
      <tfoot role="presentation">
        <tr role="presentation">
          <td role="presentation" className="inv-frame-bottom" />
        </tr>
      </tfoot>
      <tbody role="presentation">
        <tr role="presentation">
          <td role="presentation" className="inv-frame-body">
            <style>{PRINT_PAGE}</style>
            <article
              className={`inv-paper${inv.status === 'void' ? ' is-void' : ''}`}
              data-testid="invoice-paper"
            >
              <header className="inv-head">
                <div className="inv-from">
                  <OakMark />
                  <div>
                    <div className="inv-company">{company?.name || 'Morales Produce'}</div>
                    {company?.addressLine1 ? <div>{company.addressLine1}</div> : null}
                    {company?.addressLine2 ? <div>{company.addressLine2}</div> : null}
                    {cityLine ? <div>{cityLine}</div> : null}
                    {contact ? <div>{contact}</div> : null}
                    {paca?.licenseNo ? (
                      <div className="inv-license">
                        PACA {t('pacaLicenseShort')} {paca.licenseNo}
                      </div>
                    ) : null}
                  </div>
                </div>
                <section className="inv-parties">
                  <div>
                    <h2>{t('billTo')}</h2>
                    <div className="inv-party-name">{inv.billTo?.name ?? inv.customerName}</div>
                    {(inv.billTo?.address ?? []).map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                  {inv.shipTo ? (
                    <div>
                      <h2>{t('shipTo')}</h2>
                      <div className="inv-party-name">{inv.shipTo.name}</div>
                      {inv.shipTo.address.map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  ) : null}
                </section>
                <div className="inv-title">
                  {headingLevel === 1 ? (
                    <h1 className="inv-word">{t('invoice')}</h1>
                  ) : (
                    <h2 className="inv-word">{t('invoice')}</h2>
                  )}
                  {inv.test ? (
                    <p className="inv-test-stamp" data-testid="test-stamp">
                      {t('testStamp')}
                    </p>
                  ) : null}
                  <table className="inv-meta">
                    <tbody>
                      <tr>
                        <th scope="row">{t('invoiceNoLabel')}</th>
                        <td>{inv.invoiceNo}</td>
                      </tr>
                      <tr>
                        <th scope="row">{t('invoiceDate')}</th>
                        <td>{day(inv.invoiceDate)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </header>

              <table className="inv-lines">
                <thead>
                  <tr>
                    <th className="num">{t('qty')}</th>
                    <th>{t('descriptionCol')}</th>
                    <th className="num">{t('unitPriceCol')}</th>
                    <th className="num">{t('amountCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.lines.map((l) => (
                    <tr key={l.id}>
                      <td className="num qty">
                        {formatQty(l.qty)} <span className="unit">{l.unit ?? ''}</span>
                      </td>
                      <td>
                        {l.description}
                        {l.creditReason ? <div className="inv-line-note">{t('creditLineTag').replace('{reason}', t(`reason_${l.creditReason}`))}</div> : null}
                        {l.qtyOrdered !== null ? (
                          <div className="inv-line-note">
                            {t('shortLine')
                              .replace('{ordered}', formatQty(l.qtyOrdered))
                              .replace('{short}', formatQty(l.qtyOrdered - l.qty))}
                          </div>
                        ) : null}
                      </td>
                      <td className="num">{money(l.unitPrice)}</td>
                      <td className="num">{money(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="inv-bottom">
                <div className="inv-notes">
                  {inv.status === 'void' ? (
                    <span className="inv-stamp void">{t('stampVoid')}</span>
                  ) : paidOff ? (
                    <span className="inv-stamp paid">{t('stampPaid')}</span>
                  ) : null}
                  {inv.notes ? (
                    <>
                      <h2>{t('notesLabel')}</h2>
                      <p className="pre">{inv.notes}</p>
                    </>
                  ) : null}
                  {delivery && (delivery.receivedBy || delivery.hasSignature) ? (
                    <div className="delivery-proof" data-testid="delivery-proof">
                      <h2>{t('receivedBy')}</h2>
                      <span>
                        {delivery.receivedBy ?? ''}
                        {delivery.deliveredAt ? ` · ${formatStamp(delivery.deliveredAt, intlLocale)}` : ''}
                      </span>
                      {delivery.hasSignature && signatureUrl ? (
                        <img className="signature-img" src={signatureUrl} alt={t('signature')} />
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <table className="inv-totals">
                  <tbody>
                    {inv.tax > 0 ? (
                      <tr>
                        <th scope="row">{t('subtotal')}</th>
                        <td>{money(inv.subtotal)}</td>
                      </tr>
                    ) : null}
                    {inv.tax > 0 ? (
                      <tr>
                        <th scope="row">{t('tax')}</th>
                        <td>{money(inv.tax)}</td>
                      </tr>
                    ) : null}
                    <tr className="inv-total">
                      <th scope="row">{t('total')}</th>
                      <td>{money(inv.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {children}

              <footer className="inv-foot">
                <p className="inv-thanks">{t('thankYou')}</p>
                {licensed ? <p className="paca">{PACA_NOTICE}</p> : null}
                {clause ? (
                  <p className="paca" data-testid="paca-clause">
                    {clause}
                  </p>
                ) : null}
              </footer>
            </article>
          </td>
        </tr>
      </tbody>
    </table>
  );
}
